/**
 * Performance optimizations to reduce bounce rate
 * Includes: lazy loading improvements, performance monitoring, and UX enhancements
 */

(function() {
  'use strict';

  // Performance monitoring
  function trackPerformance() {
    if ('performance' in window) {
      window.addEventListener('load', function() {
        setTimeout(function() {
          const perfData = performance.getEntriesByType('navigation')[0];
          if (perfData && perfData.loadEventEnd > 0) {
            const loadTime = perfData.loadEventEnd - perfData.fetchStart;
            
            // Log performance for debugging
            console.log('Page load time:', loadTime, 'ms');
            
            // Send analytics if load time is concerning (> 3 seconds)
            if (loadTime > 3000) {
              console.warn('Slow page load detected:', loadTime + 'ms');
            }
          }
        }, 0);
      });
    }
  }

  // Intersection Observer for enhanced lazy loading
  function enhanceLazyLoading() {
    if ('IntersectionObserver' in window) {
      const imageObserver = new IntersectionObserver(function(entries) {
        entries.forEach(function(entry) {
          if (entry.isIntersecting) {
            const img = entry.target;
            if (img.dataset.src) {
              img.src = img.dataset.src;
              img.removeAttribute('data-src');
            }
            imageObserver.unobserve(img);
          }
        });
      }, {
        rootMargin: '50px 0px',
        threshold: 0.01
      });

      // Observe images with data-src attribute
      document.querySelectorAll('img[data-src]').forEach(function(img) {
        imageObserver.observe(img);
      });
    }
  }

  // Preload critical resources
  function preloadCriticalResources() {
    // Preload important fonts
    const fontUrls = [
      // Add any critical font URLs here
    ];
    
    fontUrls.forEach(function(url) {
      const link = document.createElement('link');
      link.rel = 'preload';
      link.href = url;
      link.as = 'font';
      link.type = 'font/woff2';
      link.crossOrigin = 'anonymous';
      document.head.appendChild(link);
    });
  }

  // Optimize third-party scripts
  function optimizeThirdPartyScripts() {
    // Delay non-critical third-party scripts
    const delayedScripts = document.querySelectorAll('script[data-delay]');
    
    function loadDelayedScripts() {
      delayedScripts.forEach(function(script) {
        const newScript = document.createElement('script');
        newScript.src = script.dataset.src || script.src;
        newScript.async = true;
        script.parentNode.replaceChild(newScript, script);
      });
    }

    // Load after user interaction or 3 seconds
    let loaded = false;
    function triggerLoad() {
      if (!loaded) {
        loaded = true;
        loadDelayedScripts();
      }
    }

    ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart'].forEach(function(event) {
      document.addEventListener(event, triggerLoad, { once: true, passive: true });
    });

    setTimeout(triggerLoad, 3000);
  }

  // Enhanced error recovery
  function enhanceErrorRecovery() {
    // Retry failed resource loads
    document.addEventListener('error', function(event) {
      if (event.target.tagName === 'IMG' && event.target.src) {
        const img = event.target;
        const originalSrc = img.src;
        
        // Retry after 1 second
        setTimeout(function() {
          if (img.src === originalSrc) {
            img.src = img.src; // Retry the same URL
          }
        }, 1000);
      }
    }, true);
  }

  // Improve perceived performance with skeleton loading
  function initSkeletonLoading() {
    const skeletons = document.querySelectorAll('.skeleton-loader');
    
    if (skeletons.length > 0 && 'IntersectionObserver' in window) {
      const skeletonObserver = new IntersectionObserver(function(entries) {
        entries.forEach(function(entry) {
          if (entry.isIntersecting) {
            entry.target.classList.add('skeleton-loading');
            
            // Remove skeleton after content loads
            setTimeout(function() {
              entry.target.classList.remove('skeleton-loader', 'skeleton-loading');
            }, 500);
            
            skeletonObserver.unobserve(entry.target);
          }
        });
      });

      skeletons.forEach(function(skeleton) {
        skeletonObserver.observe(skeleton);
      });
    }
  }

  // Initialize all optimizations
  function init() {
    trackPerformance();
    enhanceLazyLoading();
    preloadCriticalResources();
    optimizeThirdPartyScripts();
    enhanceErrorRecovery();
    initSkeletonLoading();
  }

  // Start when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

})();